import React, { createContext, useContext, useState, useEffect } from 'react';
import { UserProfile, AttireSuggestion, SubscriptionTier } from '../types/synclothe';
import { get, set, del } from 'idb-keyval';
import { useToasts } from '../components/TactileToasts';
import { useConfirm } from './ConfirmContext';
import { usePrompt } from './PromptContext';

interface SearchHistoryItem {
  id: string;
  timestamp: string;
  profile: UserProfile;
  suggestions: AttireSuggestion[];
  wardrobeComment: string;
}

interface StylingContextType {
  currentProfile: UserProfile;
  setCurrentProfile: (profile: UserProfile) => void;
  currentSuggestions: AttireSuggestion[];
  setCurrentSuggestions: (suggestions: AttireSuggestion[]) => void;
  wardrobeComment: string;
  setWardrobeComment: (comment: string) => void;
  searchHistory: SearchHistoryItem[];
  activeHistoryId: string | null;
  setActiveHistoryId: (id: string | null) => void;
  addToHistory: (profile: UserProfile, suggestions: AttireSuggestion[], wardrobeComment: string) => void;
  updateHistoryItemSuggestions: (historyId: string, updatedSuggestions: AttireSuggestion[]) => void;
  clearHistory: () => void;
  loadHistoryItem: (item: SearchHistoryItem) => void;
  shownWardrobeIds: string[];
  setShownWardrobeIds: React.Dispatch<React.SetStateAction<string[]>>;
  lastSearchSignature: string;
  setLastSearchSignature: (sig: string) => void;
  resetRotation: () => void;
  persona?: any;
  isProcessing: boolean;
  executeStyling: (activeProfile: UserProfile) => Promise<void>;
}

const StylingContext = createContext<StylingContextType | undefined>(undefined);

export const StylingProvider: React.FC<{ 
  children: React.ReactNode; 
  subscriptionTier: SubscriptionTier; 
  persona?: any; 
  userId?: string;
  onCreditUpdate?: (credits: number) => void;
}> = ({ children, subscriptionTier, persona, userId, onCreditUpdate }) => {
  const isPremium = subscriptionTier === 'Premium';

  const getCurrentDateTime = (offsetHours: number = 0) => {
    const now = new Date();
    now.setHours(now.getHours() + offsetHours);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const getDefaultProfile = React.useCallback((): UserProfile => ({
    event: "",
    dateTime: getCurrentDateTime(4), // +4 Hour intelligent buffer
    location: "Singapore",
    venue: "Indoor (AC)",
    age: persona?.estimatedAge || 25,
    gender: persona?.estimatedGender || 'Male',
    mobility: "Medium",
    culture: "South Asian",
    style: "Minimalist",
    fabricSensitivity: false,
    wardrobeSync: isPremium,
  }), [persona?.estimatedAge, persona?.estimatedGender, isPremium]);

  const [currentProfile, setCurrentProfile] = useState<UserProfile>(getDefaultProfile);
  const [currentSuggestions, setCurrentSuggestions] = useState<AttireSuggestion[]>([]);
  const [wardrobeComment, setWardrobeComment] = useState<string>("");
  const [searchHistory, setSearchHistory] = useState<SearchHistoryItem[]>([]);
  const [activeHistoryId, setActiveHistoryId] = useState<string | null>(null);
  const [shownWardrobeIds, setShownWardrobeIds] = useState<string[]>([]);
  const [lastSearchSignature, setLastSearchSignature] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  const { showToast } = useToasts();
  const { confirm } = useConfirm();
  const { prompt } = usePrompt();

  // Sync persona demographics into profile if persona changes (Vault update)
  useEffect(() => {
      if (persona?.estimatedAge || persona?.estimatedGender) {
          setCurrentProfile(prev => ({
              ...prev,
              age: persona.estimatedAge || prev.age,
              gender: persona.estimatedGender || prev.gender
          }));
      }
  }, [persona]);

  // Hydrate from User-Scoped Storage whenever userId changes
  useEffect(() => {
    let isMounted = true;
    setIsHydrated(false);

    if (!userId) {
      setCurrentProfile(getDefaultProfile());
      setCurrentSuggestions([]);
      setWardrobeComment("");
      setSearchHistory([]);
      setActiveHistoryId(null);
      setShownWardrobeIds([]);
      setLastSearchSignature("");
      setIsHydrated(true);
      return;
    }

    async function hydrateUser() {
      try {
        const profileKey = `synclothe_last_profile_${userId}`;
        const commentKey = `synclothe_last_comment_${userId}`;
        const suggestionsKey = `synclothe_suggestions_${userId}`;
        const historyKey = `synclothe_history_${userId}`;
        const rotationKey = `synclothe_shown_wardrobe_${userId}`;
        const signatureKey = `synclothe_last_signature_${userId}`;

        // Clean up legacy persistent localStorage keys from previous versions to prevent cross-session lockout
        try {
          localStorage.removeItem(rotationKey);
          localStorage.removeItem(signatureKey);
        } catch {}

        const [savedSuggestions, savedHistory, savedComment, savedProfileStr] = await Promise.all([
          get(suggestionsKey),
          get(historyKey),
          localStorage.getItem(commentKey),
          localStorage.getItem(profileKey)
        ]);

        // Reset session rotation on browser page refresh / reload so rotation starts fresh
        try {
          if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.removeItem(rotationKey);
            window.sessionStorage.removeItem(signatureKey);
          }
        } catch {}

        if (!isMounted) return;

        if (savedProfileStr) {
          try {
            const parsedProfile = JSON.parse(savedProfileStr);
            // Ensure dateTime is fresh and not in the past
            if (!parsedProfile.dateTime || parsedProfile.dateTime < getCurrentDateTime()) {
              parsedProfile.dateTime = getCurrentDateTime(4);
            }
            setCurrentProfile({
              ...parsedProfile,
              age: persona?.estimatedAge || parsedProfile.age,
              gender: persona?.estimatedGender || parsedProfile.gender
            });
          } catch {
            setCurrentProfile(getDefaultProfile());
          }
        } else {
          setCurrentProfile(getDefaultProfile());
        }

        setShownWardrobeIds([]);
        setLastSearchSignature("");

        setCurrentSuggestions(savedSuggestions || []);
        setSearchHistory(savedHistory || []);
        setWardrobeComment(savedComment || "");
      } catch (e) {
        console.error("User storage hydration failed", e);
        if (isMounted) {
          setCurrentProfile(getDefaultProfile());
          setCurrentSuggestions([]);
          setSearchHistory([]);
          setActiveHistoryId(null);
          setWardrobeComment("");
          setShownWardrobeIds([]);
          setLastSearchSignature("");
        }
      } finally {
        if (isMounted) setIsHydrated(true);
      }
    }

    hydrateUser();
    return () => { isMounted = false; };
  }, [userId, getDefaultProfile, persona?.estimatedAge, persona?.estimatedGender]);

  // Persist to User-Scoped localStorage (Light data)
  useEffect(() => {
    if (!isHydrated || !userId) return;
    localStorage.setItem(`synclothe_last_profile_${userId}`, JSON.stringify(currentProfile));
  }, [currentProfile, isHydrated, userId]);

  useEffect(() => {
    if (!isHydrated || !userId) return;
    localStorage.setItem(`synclothe_last_comment_${userId}`, wardrobeComment);
  }, [wardrobeComment, isHydrated, userId]);

  // Session-Scoped Wardrobe Rotation (automatically cleared on tab/window close or fresh login)
  useEffect(() => {
    if (!isHydrated || !userId) return;
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.setItem(`synclothe_shown_wardrobe_${userId}`, JSON.stringify(shownWardrobeIds));
      }
    } catch {}
  }, [shownWardrobeIds, isHydrated, userId]);

  useEffect(() => {
    if (!isHydrated || !userId) return;
    try {
      if (typeof window !== 'undefined' && window.sessionStorage) {
        window.sessionStorage.setItem(`synclothe_last_signature_${userId}`, lastSearchSignature);
      }
    } catch {}
  }, [lastSearchSignature, isHydrated, userId]);

  // Persist to User-Scoped IndexedDB (Heavy data)
  useEffect(() => {
    if (!isHydrated || !userId) return;
    set(`synclothe_suggestions_${userId}`, currentSuggestions);
  }, [currentSuggestions, isHydrated, userId]);

  useEffect(() => {
    if (!isHydrated || !userId) return;
    set(`synclothe_history_${userId}`, searchHistory);
  }, [searchHistory, isHydrated, userId]);

  const addToHistory = (profile: UserProfile, suggestions: AttireSuggestion[], comment: string) => {
    const newId = Math.random().toString(36).substr(2, 9);
    const newItem: SearchHistoryItem = {
      id: newId,
      timestamp: new Date().toISOString(),
      profile: { ...profile },
      suggestions: [...suggestions],
      wardrobeComment: comment
    };

    setSearchHistory(prev => {
      const updated = [newItem, ...prev].slice(0, 10); // Keep last 10
      return updated;
    });
    setActiveHistoryId(newId);
  };

  const updateHistoryItemSuggestions = (historyId: string, updatedSuggestions: AttireSuggestion[]) => {
    setSearchHistory(prev =>
      prev.map(item =>
        item.id === historyId
          ? { ...item, suggestions: [...updatedSuggestions] }
          : item
      )
    );
  };

  const clearHistory = () => {
    setSearchHistory([]);
    setActiveHistoryId(null);
    if (userId) {
      del(`synclothe_history_${userId}`);
    }
  };

  const resetRotation = () => {
    setShownWardrobeIds([]);
    setLastSearchSignature("");
    if (userId) {
      try {
        if (typeof window !== 'undefined' && window.sessionStorage) {
          window.sessionStorage.removeItem(`synclothe_shown_wardrobe_${userId}`);
          window.sessionStorage.removeItem(`synclothe_last_signature_${userId}`);
        }
      } catch {}
      try {
        localStorage.removeItem(`synclothe_shown_wardrobe_${userId}`);
        localStorage.removeItem(`synclothe_last_signature_${userId}`);
      } catch {}
    }
  };

  const loadHistoryItem = (item: SearchHistoryItem) => {
    setActiveHistoryId(item.id);
    setCurrentProfile({
      ...item.profile,
      age: persona?.estimatedAge || item.profile.age,
      gender: persona?.estimatedGender || item.profile.gender
    });
    setCurrentSuggestions(item.suggestions);
    setWardrobeComment(item.wardrobeComment);
  };

  const executeStyling = async (activeProfile: UserProfile) => {
    if (isProcessing) return;

    // Normalize payload data for API request while preserving UI state casing
    const normalizedProfile = {
      ...activeProfile,
      event: activeProfile.event.trim().toUpperCase(),
      location: activeProfile.location.trim().toUpperCase()
    };

    // Compute current search signature to detect parameter changes vs repeated searches
    const currentSignature = `${normalizedProfile.event.trim()}|${normalizedProfile.location.trim()}|${normalizedProfile.venue}|${normalizedProfile.culture}|${normalizedProfile.style}|${normalizedProfile.fabricSensitivity}|${normalizedProfile.wardrobeSync}|${normalizedProfile.gender}|${normalizedProfile.age}`;

    let activeExcludedIds = shownWardrobeIds;
    if (currentSignature !== lastSearchSignature) {
      activeExcludedIds = [];
      setShownWardrobeIds([]);
      setLastSearchSignature(currentSignature);
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      showToast("Network connection lost. Please reconnect to generate styling recommendations.", "error");
      return;
    }

    setIsProcessing(true);
    setCurrentSuggestions([]);
    setWardrobeComment("");

    try {
      const isMobileDevice = typeof window !== 'undefined' && window.innerWidth < 768;

      const response = await fetch('/api/getSuggestions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': userId || '' },
        body: JSON.stringify({ 
          ...normalizedProfile, 
          selectedTier: subscriptionTier,
          excludedWardrobeIds: activeExcludedIds,
          isMobile: isMobileDevice,
          clientDevice: isMobileDevice ? 'mobile' : 'desktop'
        })
      });
      
      let data: any = null;
      try {
        data = await response.json();
      } catch (jsonErr) {
        data = { error: response.statusText || "Gateway Communication Error" };
      }

      if (response.status === 403) {
          const choice = await confirm("Intelligence Exhausted", "Your logical orchestrations have reached their limit. Would you like to refuel your credits? [Confirm = Buy / Cancel = Use Ref Code]");
          if (choice) {
              window.location.href = "/subscription";
          } else {
              const ref = await prompt("Logical Refuel", "Enter a unique Intelligence Refill Code to continue orchestration:", "REFILL_CODE");
              if (ref) {
                  const res = await fetch('/api/manualActivateSubscription', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ refcode: ref })
                  });
                  if (res.ok) {
                      showToast("Logic core refueled. Re-initializing...", "success");
                      setTimeout(() => window.location.reload(), 1500);
                  } else {
                      showToast("Invalid logic code.", "error");
                  }
              }
          }
          return;
      }

      if (response.status === 429 || response.status === 400 || response.status === 401) {
          showToast(data?.error || "Request rate limit or validation limit reached. Please wait a moment.", "error");
          return;
      }

      if (!response.ok) {
        throw new Error(data?.error || "Styling Error");
      }

      setCurrentSuggestions(data.attire || []);
      setWardrobeComment(data.wardrobeComment || "");

      // Register used wardrobe items to current session rotation (or reset cycle if full closet rotated)
      if (data.attire && data.attire.length > 0) {
        const newlyShownIds = data.attire
          .filter((item: any) => item.wardrobeIds && item.wardrobeIds.length > 0)
          .flatMap((item: any) => item.wardrobeIds);
        if (newlyShownIds.length > 0) {
          setShownWardrobeIds(prev => {
            const baseList = data.rotationReset ? [] : prev;
            const combined = [...baseList, ...newlyShownIds];
            return combined.filter((val, idx) => combined.indexOf(val) === idx);
          });
        } else if (data.rotationReset) {
          setShownWardrobeIds([]);
        }
      }
      
      // Add to persistent history
      if (data.attire && data.attire.length > 0) {
        addToHistory(normalizedProfile, data.attire, data.wardrobeComment || "");
      }

      if (data.creditsRemaining !== undefined && onCreditUpdate) {
          onCreditUpdate(data.creditsRemaining);
      }
      showToast("✨ Intelligence orchestration complete. View your looks.", "success");
    } catch (error: any) {
      if ((typeof navigator !== 'undefined' && !navigator.onLine) || error.name === "TypeError" || (error.message && error.message.toLowerCase().includes("failed to fetch"))) {
        showToast("Network connection disconnected. Check your internet access.", "error");
        setIsProcessing(false);
        return;
      }
      // Azure Gateway Timeout / Server Fail-Safe: Fallback to 3 looks using digital closet & complementary concepts
      if (activeProfile.wardrobeSync) {
        try {
          const wRes = await fetch(`/api/getWardrobe?userId=${userId}&tier=${subscriptionTier}`, {
            headers: { 
              'x-user-id': userId || '',
              'x-selected-tier': subscriptionTier 
            }
          });
          if (wRes.ok) {
            const wData = await wRes.json();
            const closetItems: any[] = Array.isArray(wData) ? wData : (wData?.items || []);
            if (closetItems.length > 0) {
              const eligible = closetItems.filter((ci: any) => !activeExcludedIds.includes(ci.id));
              const cycleWrapped = eligible.length === 0;
              const pool = cycleWrapped ? closetItems : eligible;
              const selectedClosetItems = pool.slice(0, 3);

              const fallbackSuggestions: AttireSuggestion[] = selectedClosetItems.map((chosenItem: any, idx: number) => ({
                id: `${chosenItem.id}_fallback_${idx + 1}`,
                wardrobeIds: [chosenItem.id],
                wardrobePhotos: chosenItem.imageUrl ? [chosenItem.imageUrl] : [],
                name: chosenItem.name || `Curated Digital Closet Look ${idx + 1}`,
                description: `Curated directly from your digital closet vault for ${activeProfile.event} in ${activeProfile.location} with ${activeProfile.style} accents.`,
                previewUrl: chosenItem.imageUrl || "/empty_filler.jpg",
                fromWardrobe: true,
                isAiGenerated: false,
                suggestionCategory: 'from_wardrobe_combo',
                styleCategory: (chosenItem.formality >= 7 ? 'Formal' : 'Casual') as any,
                visualPrompt: `A full-body standing fashion editorial photo of a model wearing ${chosenItem.name || 'curated attire'} styled for ${activeProfile.event}.`,
                attributes: {
                  contextual: `Event: ${activeProfile.event}`,
                  cultural: activeProfile.culture,
                  physical: "Selected from your active digital closet vault",
                  aesthetic: activeProfile.style
                }
              }));

              const conceptTemplates = [
                {
                  name: "Modern Smart Casual Architecture",
                  description: `An elevated contemporary ensemble calibrated for ${activeProfile.event} in ${activeProfile.location}.`,
                  styleCategory: "Casual" as const
                },
                {
                  name: "Elevated Heritage Fusion",
                  description: `A distinctive styling balancing occasion formality and modern presence for ${activeProfile.event} in ${activeProfile.venue}.`,
                  styleCategory: "Formal" as const
                }
              ];

              while (fallbackSuggestions.length < 3) {
                const tpl = conceptTemplates[fallbackSuggestions.length - 1] || conceptTemplates[0];
                const slot = fallbackSuggestions.length + 1;
                fallbackSuggestions.push({
                  id: `fallback_concept_${slot}_${Date.now()}`,
                  wardrobeIds: [],
                  name: tpl.name,
                  description: tpl.description,
                  previewUrl: "/empty_filler.jpg",
                  fromWardrobe: false,
                  isAiGenerated: false,
                  suggestionCategory: 'ai_recommended',
                  styleCategory: tpl.styleCategory,
                  visualPrompt: `A full-body standing photo of a model wearing ${tpl.name} for ${activeProfile.event}.`,
                  attributes: {
                    contextual: `Event: ${activeProfile.event}`,
                    cultural: activeProfile.culture,
                    physical: "Balanced Proportions",
                    aesthetic: activeProfile.style
                  }
                });
              }

              const usedIds = selectedClosetItems.map((ci: any) => ci.id);
              setCurrentSuggestions(fallbackSuggestions);
              setWardrobeComment(`Stylist Note: We curated ${selectedClosetItems.map((ci: any) => ci.name).join(', ')} from your digital closet alongside complementary styling options for ${activeProfile.event}.`);
              setShownWardrobeIds(prev => {
                const baseList = cycleWrapped ? [] : prev;
                const combined = [...baseList, ...usedIds];
                return combined.filter((val, idx) => combined.indexOf(val) === idx);
              });
              addToHistory(normalizedProfile, fallbackSuggestions, `Stylist Note: Curated ${selectedClosetItems.map((ci: any) => ci.name).join(', ')} from your digital closet.`);
              showToast("✨ Curated 3 looks using your digital closet.", "success");
              return;
            }
          }
        } catch (fallbackErr) {
          console.warn("Digital closet fallback query notice:", fallbackErr);
        }
      }
      showToast(`Logic Error: ${error.message}`, "error");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <StylingContext.Provider value={{
      currentProfile, setCurrentProfile,
      currentSuggestions, setCurrentSuggestions,
      wardrobeComment, setWardrobeComment,
      searchHistory, activeHistoryId, setActiveHistoryId,
      addToHistory, updateHistoryItemSuggestions, clearHistory,
      loadHistoryItem,
      shownWardrobeIds, setShownWardrobeIds,
      lastSearchSignature, setLastSearchSignature,
      resetRotation,
      persona,
      isProcessing,
      executeStyling
    }}>
      {children}
    </StylingContext.Provider>
  );
};

export const useStyling = () => {
  const context = useContext(StylingContext);
  if (context === undefined) {
    throw new Error('useStyling must be used within a StylingProvider');
  }
  return context;
};
